/**
 * Admin Disputes API client — CON-PAYMENTS-004.
 *
 * Wraps the platform endpoints:
 *   - GET  /admin/disputes               → {@link listAdminDisputes}
 *   - GET  /admin/disputes/{id}          → {@link getAdminDispute}
 *   - GET  /admin/disputes/{id}/evidence → {@link getDisputeEvidenceFiles}
 *   - POST /admin/orders/{orderId}/disputes/{disputeId}/evidence
 *                                        → {@link submitDisputeEvidence}
 *
 * Every helper:
 *   1. Calls the shared axios instance (no per-call auth headers).
 *   2. Unwraps the success envelope into the typed payload.
 *   3. Translates non-2xx responses into {@link ApiError}.
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  Dispute,
  DisputeEvidenceFile,
  DisputeListParams,
  DisputeListResult,
  DisputePaginationMeta,
  SubmitDisputeEvidencePayload,
} from '@dfq/shared/types/payments';

import { apiClient } from '../client';
import { throwAsApiError, unwrap } from '../_envelope';

const COLLECTION_PATH = '/admin/disputes';

function itemPath(id: number): string {
  return `${COLLECTION_PATH}/${encodeURIComponent(String(id))}`;
}

function evidencePath(orderId: number, disputeId: number): string {
  return `/admin/orders/${encodeURIComponent(String(orderId))}/disputes/${encodeURIComponent(String(disputeId))}/evidence`;
}

/**
 * Build the query-string parameter map for a disputes list request.
 */
function buildListParams(
  params: DisputeListParams,
): Record<string, string | number | boolean | (string | number | boolean)[]> {
  const out: Record<string, string | number | boolean | (string | number | boolean)[]> = {};

  if (params.page !== undefined) out.page = params.page;
  if (params.perPage !== undefined) out.per_page = params.perPage;
  if (params.sortBy !== undefined) out.sort_by = params.sortBy;
  if (params.sortDirection !== undefined) out.sort_direction = params.sortDirection;

  const filters = params.filters ?? {};
  if (filters.status !== undefined && filters.status !== '') {
    out['filters[status]'] = filters.status;
  }
  if (filters.search !== undefined && filters.search !== '') {
    out['filters[search]'] = filters.search;
  }

  return out;
}

// ---------------------------------------------------------------------------
// Wire-shape data envelopes
// ---------------------------------------------------------------------------

interface DisputeListData {
  disputes: Dispute[];
}

interface DisputeData {
  dispute: Dispute;
}

interface EvidenceFilesData {
  evidence_files: DisputeEvidenceFile[];
}

// ---------------------------------------------------------------------------
// Fetch list
// ---------------------------------------------------------------------------

/**
 * Fetch a paginated list of all disputes for the admin.
 */
export async function listAdminDisputes(params: DisputeListParams = {}): Promise<DisputeListResult> {
  try {
    const response = await apiClient.get<ApiEnvelope<DisputeListData>>(COLLECTION_PATH, {
      params: buildListParams(params),
    });

    const data = unwrap(response.data, response.status);
    const paginationRaw = response.data.meta?.pagination;
    const disputes = data.disputes ?? [];
    const pagination: DisputePaginationMeta =
      paginationRaw && typeof paginationRaw === 'object'
        ? (paginationRaw as DisputePaginationMeta)
        : {
            total: disputes.length,
            perPage: params.perPage ?? disputes.length,
            currentPage: params.page ?? 1,
            lastPage: 1,
            from: disputes.length > 0 ? 1 : null,
            to: disputes.length > 0 ? disputes.length : null,
          };

    return { disputes, pagination };
  } catch (error) {
    throwAsApiError(error);
  }
}

// ---------------------------------------------------------------------------
// Fetch single
// ---------------------------------------------------------------------------

/**
 * Fetch a single dispute by id.
 */
export async function getAdminDispute(disputeId: number): Promise<Dispute> {
  try {
    const response = await apiClient.get<ApiEnvelope<DisputeData>>(itemPath(disputeId));
    const data = unwrap(response.data, response.status);
    if (!data.dispute) {
      throw new Error('Dispute envelope missing `data.dispute`');
    }
    return data.dispute;
  } catch (error) {
    throwAsApiError(error);
  }
}

// ---------------------------------------------------------------------------
// Fetch evidence files
// ---------------------------------------------------------------------------

/**
 * Fetch all evidence files previously submitted for a dispute.
 */
export async function getDisputeEvidenceFiles(
  disputeId: number,
): Promise<DisputeEvidenceFile[]> {
  try {
    const response = await apiClient.get<ApiEnvelope<EvidenceFilesData>>(
      `${itemPath(disputeId)}/evidence`,
    );
    const data = unwrap(response.data, response.status);
    return data.evidence_files ?? [];
  } catch (error) {
    throwAsApiError(error);
  }
}

// ---------------------------------------------------------------------------
// Submit evidence
// ---------------------------------------------------------------------------

/**
 * POST multipart evidence submission to Stripe for the given dispute.
 *
 * Files are sent as multipart/form-data because the platform uploads them
 * to Stripe and stores them on S3 — the server needs the raw bytes.
 */
export async function submitDisputeEvidence(
  orderId: number,
  disputeId: number,
  payload: SubmitDisputeEvidencePayload,
): Promise<Dispute> {
  try {
    const form = new FormData();
    form.append('customer_communication', payload.customer_communication);
    if (payload.uncategorized_text) {
      form.append('uncategorized_text', payload.uncategorized_text);
    }
    payload.service_files.forEach((file) => {
      form.append('service_files[]', file);
    });
    payload.shipping_files.forEach((file) => {
      form.append('shipping_files[]', file);
    });

    const response = await apiClient.post<ApiEnvelope<DisputeData>>(
      evidencePath(orderId, disputeId),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    const data = unwrap(response.data, response.status);
    if (!data.dispute) {
      throw new Error('Dispute envelope missing `data.dispute`');
    }
    return data.dispute;
  } catch (error) {
    throwAsApiError(error);
  }
}
